import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import ClientForm, { clientSchema } from './ClientForm';
import { IClientRequest, IClient } from "@ontime/types";
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import ClientService from '../../services/ClientService';
import GridTable, { defaultPaginationData, GridColumnType, PaginationType } from '../../gridTable/GridTable';
import { IAppointmentUser } from "@ontime/types";
import { z } from 'zod';

const ClientList: React.FC<{
    state: 'mount' | 'unmount' | 'mountByFilter';
    CustomTitle?: string;
    loading: boolean;
    setLoading: (input: boolean) => void;
    handleClose?: () => void;
    handleSelect?: (input: IClient) => void;
    userInputFilter?: IAppointmentUser | null;
    showInfoToggleButton?: boolean;
    hiddenClientId?: string;
}> = ({ state, CustomTitle, loading, setLoading, handleSelect, userInputFilter, handleClose, showInfoToggleButton, hiddenClientId }) => {

    const organization = useSelector(selectSelectedOrganization);

    const [clients, setClients] = useState<IClient[]>([]);
    const [paginationData, setPaginationData] = useState<PaginationType>(defaultPaginationData);

    const defaultCreateRowDto = { clientId: '', phone: '', firstName: '', email: '', code: '' };
    const [createRowDto, setCreateRowDto] = useState<IClientRequest>(defaultCreateRowDto);
    const { enqueueSnackbar } = useSnackbar();

    const [formOpen, setFormOpen] = useState<{ open: boolean; client?: IClient | null }>({ open: false });

    const closeForm = (input?: IClient) => {
        if (handleSelect && input) {
            handleSelect(input);
        }
        else {
            setFormOpen({ open: false });
            if (input?.clientId) {
                if (clients.find((item) => item.clientId === input.clientId)) {
                    setClients([input, ...clients.filter((item) => item.clientId !== input.clientId)]);
                }
                else {
                    setClients([input, ...clients]);
                }
            }
        }
    };

    const handleSetClient = (input?: IClient | null) => {
        console.log('handleSetClient', input)
        if (input?.clientId) {
            if (clients.find((item) => item.clientId === input.clientId)) {
                setClients([input, ...clients.filter((item) => item.clientId !== input.clientId)]);
            }
            // else {
            //     setClients([input, ...clients]);
            // }
        }

    };

    useEffect(() => {
        if (userInputFilter) {
            setCreateRowDto({ ...createRowDto, firstName: userInputFilter.name ?? '', lastName: userInputFilter.surname, phone: userInputFilter.phoneNumber, });
        }
    }, [userInputFilter]);

    const [deleteLoading, setDeleteLoading] = useState<string[]>([]);

    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const onClickDeleteFolder = () => {
        setShowDeleted(!showDeleted);
    }

    const getAll = async () => {
        if (state === 'mountByFilter' && organization.organizationId && !createRowDto.phone && !createRowDto.firstName && !createRowDto.lastName && !createRowDto.code) {
            enqueueSnackbar('Set one of filters', { variant: 'error' });
        }
        else {

            setLoading(true);
            try {
                const filterDto = {
                    isDeleted: showDeleted,
                    phone: createRowDto.phone,
                    email: createRowDto.email,
                    firstName: createRowDto.firstName,
                    lastName: createRowDto.lastName,
                    code: createRowDto.code,
                    hiddenClientId: hiddenClientId,
                };
                const response = await ClientService.GetList(organization.organizationId, filterDto);
                setClients(response);
            } catch (error) {
                console.error(error, { variant: 'error' });
            } finally {
                setLoading(false);
            }
        }

    };

    const handleDelete = async (input: IClient) => {
        setDeleteLoading([...deleteLoading, input.clientId ?? '']);

        try {
            await ClientService.Delete(organization.organizationId ?? '', input.clientId ?? '', input.isDeleted ?? false);

            setClients(clients.filter((item) => item.clientId !== input.clientId));

        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setDeleteLoading(deleteLoading.filter((item) => item !== input.clientId));
        }
    };

    const clientType: GridColumnType<IClient> = {
        clientId: { dontShow: true },
        organizationId: { type: "Integer", dontShow: true },
        phone: { type: "Phone", header: 'Phone', searchableOnTop: true, showInCreateRow: true },
        email: { type: "Text", header: 'Email', searchableOnTop: true, showInCreateRow: true },
        firstName: { type: "Text", header: 'FirstName', searchableOnTop: true, showInCreateRow: true, required: true },
        lastName: { type: "Text", header: 'LastName', searchableOnTop: true, showInCreateRow: true },
        code: { type: "Text", header: 'Code', showInCreateRow: true },
    };

    const handleNew = () => {
        setFormOpen({ open: true })
    };

    const handleEdit = (input: IClient) => {
        setFormOpen({ open: true, client: input });
    };

    useEffect(() => {
        if (state === 'mount' && organization.organizationId) {
            console.log('mount')
            getAll();
        }
        else if (state === 'mountByFilter' && organization.organizationId &&
            (createRowDto.phone || createRowDto.firstName || createRowDto.lastName || createRowDto.code)) {
            console.log('mountByFilter')
            getAll();
        }
        else if (state === 'unmount') {
            console.log('unmount')
            setCreateRowDto(defaultCreateRowDto);
            setClients([]);
            setPaginationData(defaultPaginationData);
        };
    }, [state, paginationData.skipCount, paginationData.maxResultCount, showDeleted]);


    const handleSave = async (data: IClientRequest) => {
        try {
            const validated = clientSchema.parse(data);

            setLoading(true);

            const formData = {
                ...data,
                organizationId: organization.organizationId
            };

            try {
                const response = await ClientService.Create(organization.organizationId ?? '', formData);

                closeForm(response);
            } catch (error) {
                console.error(error, { variant: 'error' });
            } finally {
                setLoading(false);
            }
        } catch (error) {
            if (error instanceof z.ZodError) {
                error.issues.map(item => {
                    enqueueSnackbar(item.message, { variant: 'error' })
                }
                );
            }
        }
    };

    return (
        formOpen.open ?
            <ClientForm clientInput={formOpen.client} closeForm={closeForm} handleSetClient={handleSetClient} />
            :
            <GridTable<IClient, IClientRequest, IClientRequest>
                gridTitle={CustomTitle ? CustomTitle : 'Client list'}
                gridTitleInfo='Manage your client records, including contact details and status'
                loading={loading}
                list={clients.map((item) => ({ ...item, id: item.clientId }))}
                inputType={clientType}
                onClickNew={handleNew}
                onEditRow={handleEdit}
                onDeleteRow={handleDelete}
                deleteLoading={deleteLoading}
                paginationData={paginationData}
                setPaginationData={setPaginationData}
                handleCreateRow={handleSave}
                onClickRow={handleSelect}
                createRowDto={createRowDto}
                setCreateRowDto={setCreateRowDto}
                searchButtonInCreateRow={getAll}
                onClickBack={handleClose}
                isOnDialog={true}
                showInfoToggleButton={showInfoToggleButton}
                onClickDeleteFolder={onClickDeleteFolder}
                showDeleted={showDeleted}
            />
    );
};

export default ClientList;
